import { mockLoans, mockBrokers, mockOffices, mockBrokerUsers } from '../mockData';

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Transform mockLoans into the pipeline shape the page expects
const BROKER_COMP_BPS = 275; // 2.75% default broker compensation

const buildPipelineResponse = () => {
  // Columns aligned with real pipeline API fields + recommended additions
  const columns = [
    // --- From real API ---
    { id: 1,  name: 'Loan Number' },
    { id: 2,  name: 'Borrower' },
    { id: 3,  name: 'Property City' },
    { id: 4,  name: 'Property State' },
    { id: 5,  name: 'Lock Expires' },
    { id: 6,  name: 'Loan Amount' },
    { id: 7,  name: 'Current Stage' },
    { id: 8,  name: 'Intended Usage' },
    { id: 9,  name: 'Broker Comp' },
    { id: 10, name: 'Loan Originator' },
    // --- Recommended additions ---
    { id: 11, name: 'Loan Program' },
    { id: 12, name: 'Rate' },
    { id: 13, name: 'Lock Status' },
    { id: 14, name: 'Loan Purpose' },
    { id: 15, name: 'Credit Score' },
    { id: 16, name: 'LTV' },
    { id: 17, name: 'Conditions' },
    { id: 18, name: 'Est. Closing' },
    { id: 19, name: 'Submitted' },
  ];

  const getLockStatus = (loan) => {
    if (!loan.loanDetails?.lockDate) return 'Float';
    if (!loan.lockExpiration) return 'Float';
    const exp = new Date(loan.lockExpiration);
    if (exp < new Date()) return 'Expired';
    return 'Locked';
  };

  const loans = mockLoans.map(loan => {
    const comp = (loan.loanAmount || 0) * (BROKER_COMP_BPS / 10000);
    return {
      loanId: loan.id,
      fields: {
        'Loan Number': loan.loanNumber,
        'Borrower': loan.borrower?.lastName || loan.borrowerName?.split(' ').pop() || '—',
        'Property City': loan.property?.city || '—',
        'Property State': loan.property?.state || '—',
        'Lock Expires': loan.lockExpiration || '—',
        'Loan Amount': loan.loanAmount ? `$${loan.loanAmount.toLocaleString()}` : '—',
        'Current Stage': loan.status,
        'Intended Usage': loan.property?.occupancy || '—',
        'Broker Comp': `$${comp.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
        'Loan Originator': loan.loanOfficer,
        'Loan Program': loan.loanDetails?.loanProduct?.programName || '—',
        'Rate': loan.loanDetails?.loanProduct?.noteRate ? `${loan.loanDetails.loanProduct.noteRate}%` : '—',
        'Lock Status': getLockStatus(loan),
        'Loan Purpose': loan.loanDetails?.loanPurpose || '—',
        'Credit Score': loan.borrower?.creditScore || '—',
        'LTV': loan.loanDetails?.loanToValueRatio ? `${loan.loanDetails.loanToValueRatio}%` : '—',
        'Conditions': loan.conditionsCount > 0 ? `${loan.conditionsCount} outstanding` : 'None',
        'Est. Closing': loan.loanDetails?.estimatedClosingDate || '—',
        'Submitted': loan.submittedDate || '—',
      },
      // Raw numeric values for metrics (not displayed, used for summary calc)
      _raw: {
        loanAmount: loan.loanAmount || 0,
        brokerComp: comp,
        creditScore: loan.borrower?.creditScore || 0,
      },
    };
  });

  // Compute summary metrics for broker dashboard
  const now = new Date();
  const sevenDaysOut = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  const totalVolume = mockLoans.reduce((sum, l) => sum + (l.loanAmount || 0), 0);
  const totalCompensation = totalVolume * (BROKER_COMP_BPS / 10000);
  const avgLoanAmount = mockLoans.length > 0 ? totalVolume / mockLoans.length : 0;

  const locksExpiringSoon = mockLoans.filter(l => {
    if (!l.lockExpiration) return false;
    const exp = new Date(l.lockExpiration);
    return exp >= now && exp <= sevenDaysOut;
  }).length;

  const totalConditions = mockLoans.reduce((sum, l) => sum + (l.conditionsCount || 0), 0);

  const statusCounts = {};
  mockLoans.forEach(l => { statusCounts[l.status] = (statusCounts[l.status] || 0) + 1; });

  const closedLoans = mockLoans.filter(l => l.status === 'Closed' || l.status === 'Clear to Close').length;
  const submittedLast30 = mockLoans.filter(l => {
    if (!l.submittedDate) return false;
    const d = new Date(l.submittedDate);
    return (now - d) <= 30 * 24 * 60 * 60 * 1000;
  }).length;

  const summary = {
    totalLoans: mockLoans.length,
    totalVolume,
    totalCompensation,
    avgLoanAmount,
    locksExpiringSoon,
    totalConditions,
    statusCounts,
    closedLoans,
    submittedLast30,
    brokerCompBps: BROKER_COMP_BPS,
  };

  return { columns, loans, summary };
};

// Route map: URL pattern → mock response
const mockRoutes = [
  { match: (url) => url === '/pipeline', handler: () => buildPipelineResponse() },
  { match: (url) => url === '/broker-companies', handler: () => mockBrokers },
  { match: (url) => /^\/broker-companies\/(\d+)$/.test(url), handler: (url) => {
    const id = parseInt(url.split('/').pop());
    return mockBrokers.find(b => b.id === id) || null;
  }},
  { match: (url) => /^\/broker-companies\/(\d+)\/offices$/.test(url), handler: (url) => {
    const id = parseInt(url.split('/')[2]);
    return mockOffices.filter(o => o.brokerId === id);
  }},
  { match: (url) => /^\/broker-companies\/(\d+)\/users$/.test(url), handler: () => mockBrokerUsers },
  { match: (url) => url === '/broker-companies/my-company', handler: () => mockBrokers[0] },
  { match: (url) => url.startsWith('/broker-companies/by-nmls'), handler: (url) => {
    const query = url.includes('?') ? url.split('?')[1] : '';
    const nmls = new URLSearchParams(query).get('companyNmls')?.trim();
    return mockBrokers.find((b) => String(b.nmls) === String(nmls || '')) || null;
  }},
  { match: (url) => url === '/broker-companies/my-company/offices', handler: () => mockOffices.filter(o => o.brokerId === 1) },
  { match: (url) => url === '/broker-companies/my-company/users', handler: () => mockBrokerUsers },
  { match: (url) => url === '/configuration/feature-flags', handler: () => ({
    brokerPortal: true,
    scenarioPricer: true,
    aiUnderwriting: false,
    newLoanButton: true,
    pipeline: true,
    dashboard: true,
    users: true,
    offices: true,
    compensation: true,
  })},
];

export const mockBaseQuery = async (args) => {
  const url = typeof args === 'string' ? args : args.url;
  const method = (typeof args === 'object' ? args.method : 'GET') || 'GET';

  await delay(300);

  // For mutations, return the updated object so RTK Query cache updates
  if (method !== 'GET') {
    const body = typeof args === 'object' ? args.body : null;

    // PUT /broker-companies/{id} — update broker/office in memory and return merged object
    const brokerPutMatch = url.match(/^\/broker-companies\/([^/]+)$/);
    if (method === 'PUT' && brokerPutMatch && body) {
      const targetId = brokerPutMatch[1];
      // Find in mockBrokers or mockOffices by id (support both number and guid-style ids)
      let target = mockBrokers.find(b => String(b.id) === targetId);
      if (!target) target = mockOffices.find(o => String(o.id) === targetId);
      if (target) {
        Object.assign(target, body);
        return { data: { ...target } };
      }
    }

    // PUT /broker-companies/my-company — update the first mock broker
    if (method === 'PUT' && url === '/broker-companies/my-company' && body) {
      Object.assign(mockBrokers[0], body);
      return { data: { ...mockBrokers[0] } };
    }

    // POST /broker-companies/{id}/approve — update office and return it
    const approveMatch = url.match(/^\/broker-companies\/([^/]+)\/approve$/);
    if (method === 'POST' && approveMatch && body) {
      const targetId = approveMatch[1];
      const office = mockOffices.find(o => String(o.id) === targetId);
      if (office) {
        Object.assign(office, body, { status: 'Active' });
        return { data: { ...office } };
      }
    }

    return { data: body || { success: true } };
  }

  for (const route of mockRoutes) {
    if (route.match(url)) {
      const data = route.handler(url);
      return { data };
    }
  }

  console.warn(`[Mock API] No handler for ${method} ${url}`);
  return { data: null };
};
